import type { Status } from '@googlemaps/react-wrapper'
import type { GoogleServices } from 'components/common/molecules/google-map/google-map'

import { generateAddress } from '@rentspree/helper'
import { useState, useEffect, useContext } from 'react'

import { usePrevious } from '@hooks'
import { GoogleMap, MapCustomMarker, NearbyPlaces } from 'components'
import { PANE_LAYER } from 'components/common/molecules/google-map/constants'
import { ListingIdContext } from 'context/listingIdContext'
import { useTracker } from 'store/@hooks/use-tracker'
import {
  LOCATION_CATEGORIES,
  MAXIMUM_ZOOM_LEVEL,
  DEG_TO_RAD_DIVISOR,
  LOCATION_CATEGORY_ICONS,
  MEAN_RADIUS_EARTH_IN_KM,
  NEARBY_PLACES_RADIUS,
  ZOOM_FACTOR,
} from 'store/public-listing/constant'
import { LISTING_PUBLIC_PAGE } from 'tracker/events'

import { EVENT_NAME } from './constants'
import { MarkerIcon } from './marker-icon'
import { MarkerInfo } from './marker-info'
import { LocationPanelSubject, LocationPanel, LocationMap, LocationContent } from './styled'

const style = {
  width: '100%',
  height: '100%',
}

interface ListingMapProps extends google.maps.MapOptions {
  position: google.maps.LatLngLiteral
  propertyAddress: PropertyAddress
  nearbyPlaces: Record<string, Place[]>
}

const convertPlaceResultToPlaces = (
  type: string,
  results: google.maps.places.PlaceResult[] | null,
): Place[] => {
  if (!(results && results.length > 0)) return []

  const places = results
    .filter(
      (place) =>
        place.name && place.place_id && place.vicinity && place.geometry && place.geometry.location,
    )
    .map((place) => ({
      index: place.place_id || '',
      name: place.name || '',
      address: place.vicinity || '',
      type,
      location: {
        lat: place?.geometry?.location?.lat() || 0,
        lng: place?.geometry?.location?.lng() || 0,
      },
    }))

  return places
}

export const ListingMap: React.FC<ListingMapProps> = ({
  position,
  nearbyPlaces, // deprecated
  zoom,
  propertyAddress,
  ...options
}) => {
  const track = useTracker()
  const listingId = useContext(ListingIdContext)
  const categories = Object.keys(LOCATION_CATEGORIES)
  const { street, unitNumber } = propertyAddress

  const [map, setMap] = useState<google.maps.Map>() // eslint-disable-line
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService>()
  // const [googleMaps, setGoogleMaps] = useState<typeof google.maps>() // eslint-disable-line
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [placesByCategories, setPlacesByCategories] = useState<Record<string, number>>({})
  const [nearbyPositions, setNearbyPositions] = useState<Place[]>([])
  const [googleDistanceMatrixAdaptor, setGoogleDistanceMatrixAdaptor] = useState<IDistanceService>()
  const [renderNearbyPositions, setRenderNearbyPositions] = useState<Place[]>([])
  const [clickPropertyName, setClickPropertyName] = useState({})
  const [isFullScreen, setIsFullScreen] = useState(false)
  const prevRenderNearbyPositions = usePrevious(renderNearbyPositions)

  const postMessage = (message: IframeMessage<null>) => {
    const window = global.window
    window?.parent?.postMessage(message, '*')
  }

  const callback = (status: Status) => {
    postMessage({ eventName: EVENT_NAME[status], payload: null })
  }

  const trackMapInteraction = () =>
    track(LISTING_PUBLIC_PAGE.CLICK_ON_MAP, { listing_id: listingId })

  const findNearbyPlaces = (category: string) => {
    if (!placesService) throw new Error('Place service is not defined')
    if (!LOCATION_CATEGORIES[category]) throw new Error(`Category "${category}" is not defined`)
    const type = LOCATION_CATEGORIES[category]

    if (placesByCategories[category] !== undefined) return

    const request: google.maps.places.PlaceSearchRequest = {
      location: position,
      radius: NEARBY_PLACES_RADIUS,
      type,
    }

    placesService.nearbySearch(request, (results) => {
      const places = convertPlaceResultToPlaces(type, results)
      setPlacesByCategories((prev) => ({
        ...prev,
        [category]: places.length,
      }))
      setNearbyPositions((prev) => [...prev, ...places])
      setRenderNearbyPositions((prev) => [...prev, ...places])
    })
  }

  const onCustomMarkerClick = (infoId: string) => () => {
    const info = document.getElementById(infoId)
    if (info?.classList.contains('active')) {
      info?.classList.remove('active')
    } else {
      info?.classList.add('active')
    }
  }

  const handleClickCategory = (categoryName: string) => {
    const isSelected = selectedCategories.includes(categoryName)

    findNearbyPlaces(categoryName)

    if (!isSelected) {
      // const positions = nearbyPlaces[category]
      // setNearbyPositions((prev) => [...prev, ...positions])
      setSelectedCategories((prev) => [...prev, categoryName])
      track(LISTING_PUBLIC_PAGE.CLICK_NEARBY_PLACE, {
        category: categoryName.toLowerCase(),
        listing_id: listingId,
      })
    } else {
      // setNearbyPositions((prev) => prev.filter((item) => item.type !== categoryName))
      setSelectedCategories((prev) => prev.filter((category) => category !== categoryName))
    }
  }

  const handleMapRender = (services: GoogleServices) => {
    if (!map) setMap(services.map)
    if (!googleDistanceMatrixAdaptor) {
      setGoogleDistanceMatrixAdaptor(services.distanceMatrixService)
    }
    if (!placesService) setPlacesService(services.places)
  }

  const haverSine = (
    maximumLat: number,
    minimumLat: number,
    maximumLng: number,
    minimumLng: number,
  ) => {
    const deltaLat = maximumLat - minimumLat
    const deltaLng = maximumLng - minimumLng
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(minimumLat) * Math.cos(maximumLat) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return MEAN_RADIUS_EARTH_IN_KM * c
  }

  const optimizeZoomLevel = (places: Place[], height: number, width: number) => {
    const placesWithDelta = places.map((place) => ({
      ...place,
      deltaLat: Math.abs(position.lat - place.location.lat),
      deltaLng: Math.abs(position.lng - place.location.lng),
    }))

    const placeMaximumDeltaLat = placesWithDelta.reduce((previous, current) => {
      if (previous.deltaLat > current.deltaLat) {
        return previous
      }
      return current
    })

    const placeMaximumDeltaLng = placesWithDelta.reduce((previous, current) => {
      if (previous.deltaLng > current.deltaLng) {
        return previous
      }
      return current
    })

    const placeWithMaximumDelta = {
      lat: placeMaximumDeltaLat.location.lat,
      lng: placeMaximumDeltaLng.location.lng,
    }

    const reversedPlaceWithMaximumDelta = {
      lat: position.lat + (position.lat - placeMaximumDeltaLat.location.lat),
      lng: position.lng + (position.lng - placeMaximumDeltaLng.location.lng),
    }

    const calculatedPlaces = [placeWithMaximumDelta, reversedPlaceWithMaximumDelta]

    const maximumLat = Math.max(...calculatedPlaces.map((place) => place.lat)) / DEG_TO_RAD_DIVISOR
    const minimumLat = Math.min(...calculatedPlaces.map((place) => place.lat)) / DEG_TO_RAD_DIVISOR
    const maximumLng = Math.max(...calculatedPlaces.map((place) => place.lng)) / DEG_TO_RAD_DIVISOR
    const minimumLng = Math.min(...calculatedPlaces.map((place) => place.lng)) / DEG_TO_RAD_DIVISOR

    const greatCircleDistance = haverSine(maximumLat, minimumLat, maximumLng, minimumLng)
    const zoomLevel = Math.floor(
      7 -
        Math.log((ZOOM_FACTOR * greatCircleDistance) / Math.sqrt(2 * (height * width))) /
          Math.log(2),
    )
    return zoomLevel >= MAXIMUM_ZOOM_LEVEL ? MAXIMUM_ZOOM_LEVEL : zoomLevel
  }

  const smoothZoom = (zoomLevel: number) => {
    const currentZoom = map?.getZoom() as number
    if (currentZoom !== zoomLevel) {
      if (currentZoom < zoomLevel) {
        map?.setOptions({ center: position, zoom: currentZoom + 1 })
      } else {
        map?.setOptions({ center: position, zoom: currentZoom - 1 })
      }
      setTimeout(() => {
        smoothZoom(zoomLevel)
      }, 80)
    }
  }

  useEffect(() => {
    if (map) {
      smoothZoom(MAXIMUM_ZOOM_LEVEL)
    }
  }, [clickPropertyName]) // eslint-disable-line

  useEffect(() => {
    setRenderNearbyPositions(
      nearbyPositions.filter(({ type }) =>
        selectedCategories.map((item) => LOCATION_CATEGORIES[item]).includes(type),
      ),
    )
  }, [selectedCategories]) // eslint-disable-line

  useEffect(() => {
    if (
      prevRenderNearbyPositions &&
      prevRenderNearbyPositions?.length >= renderNearbyPositions.length
    ) {
      return
    }
    const mapHtmlElement = map?.getDiv() as HTMLElement
    if (!mapHtmlElement) return
    const zoomLevel = optimizeZoomLevel(
      renderNearbyPositions,
      mapHtmlElement.offsetHeight,
      mapHtmlElement.offsetWidth,
    )
    if (zoomLevel && zoomLevel <= MAXIMUM_ZOOM_LEVEL) {
      smoothZoom(zoomLevel)
    }
  }, [renderNearbyPositions]) // eslint-disable-line

  const onFullScreen = () => {
    if (!isFullScreen) {
      track(LISTING_PUBLIC_PAGE.CLICK_VIEW_MAP_ON_FULLSCREEN, {
        listing_id: listingId,
      })
      setIsFullScreen(true)
    }
  }

  const onExitFullScreen = () => {
    if (isFullScreen) {
      setIsFullScreen(false)
    }
  }

  const onStreetViewFullScreen = () => {
    track(LISTING_PUBLIC_PAGE.CLICK_VIEW_MAP_ON_FULLSCREEN, {
      listing_id: listingId,
    })
  }

  return (
    <>
      <LocationContent>
        <LocationMap>
          <GoogleMap
            callback={callback}
            center={position}
            zoom={zoom}
            onClick={trackMapInteraction}
            onDragStart={trackMapInteraction}
            onMapRender={handleMapRender}
            onFullScreen={onFullScreen}
            onExitFullScreen={onExitFullScreen}
            onStreetViewFullScreen={onStreetViewFullScreen}
            mapTypeControl={false}
            streetViewControl={false}
            {...options}
            style={style}
          >
            <MapCustomMarker position={position} onClick={() => setClickPropertyName({})}>
              <MarkerInfo text={generateAddress({ street, unitNumber })} />
            </MapCustomMarker>

            {renderNearbyPositions.length > 0 &&
              renderNearbyPositions.map(({ type, name, location, index }) => {
                const markerKey = [type, location.lat, location.lng, index].join('-')
                const infoId = `${name.replace(/\s+/g, '')}-${index}-info`
                return (
                  <MapCustomMarker
                    key={`${markerKey}-custom-marker`}
                    position={location}
                    paneLayer={PANE_LAYER.overlayMouseTarget}
                    onClick={onCustomMarkerClick(infoId)}
                  >
                    <MarkerIcon
                      key={`${markerKey}-marker-icon`}
                      name={name}
                      infoId={infoId}
                      iconClassName={LOCATION_CATEGORY_ICONS[type]}
                    />
                  </MapCustomMarker>
                )
              })}
          </GoogleMap>
        </LocationMap>
        {categories.length > 0 && (
          <LocationPanel>
            <LocationPanelSubject>Click below to see what&rsquo;s nearby:</LocationPanelSubject>
            <NearbyPlaces
              categories={categories}
              onClick={handleClickCategory}
              selectedCategories={selectedCategories}
            />
          </LocationPanel>
        )}
      </LocationContent>
    </>
  )
}
